angular.module("modules:mapping").factory("Map", [
  "Restangular",
  "Collection" /* side effects */,
  "ObjectFilter",
  "$q",
  "$http",
  "API_ROOT",
  "INSTANCE_ID",
  "TILE_URL",
  "MAPBOX_USER",
  "MAPBOX_STYLE_ID",
  "MAPBOX_TILE_SIZE",
  "MAPBOX_ACCESS_TOKEN",
  "Interval",

  function (
    Restangular,
    Collection /* side effects */,
    ObjectFilter,
    $q,
    $http,
    API_ROOT,
    INSTANCE_ID,
    TILE_URL,
    MAPBOX_USER,
    MAPBOX_STYLE_ID,
    MAPBOX_TILE_SIZE,
    MAPBOX_ACCESS_TOKEN,
    Interval
  ) {
    var Maps = Restangular.all("mapping/maps");

    // add methods to single collections
    Restangular.addElementTransformer("mapping/maps", false, function (map) {
      if (!("one" in map)) return map;

      if ("collections" in map) {
        for (var i = 0; i < map.collections.length; i++) {
          Restangular.restangularizeElement(null, map.collections[i], "mapping/collections");
        }
      }

      /**
       * Creates the date range for the map, if enabled
       * @return {void}
       */
      map.createDateRangeIfEnabled = function () {
        if (!this.dateEnabled) return;

        // Create the objects to hold the date range
        // maxDate range is the object that holds the max (and min) values of the map's data,
        this.maxDateRange = {};

        // Loop through all the collections, finding the minimum date range and the maximum date range
        for (var i = 0; i < this.collections.length; i++) {
          var collection = this.collections[i];
          if (!this.visible[collection.ID] || !collection.dateRange) continue;

          var min = moment(collection.dateRange[0]),
            max = moment(collection.dateRange[1]);

          // If the date is undefined or before the minDate, set it
          if (typeof this.maxDateRange.minDate == "undefined" || min.isBefore(this.maxDateRange.minDate)) this.maxDateRange.minDate = min;

          // If the date is undefined or after the maxDate, set it
          if (typeof this.maxDateRange.maxDate == "undefined" || max.isAfter(this.maxDateRange.maxDate)) this.maxDateRange.maxDate = max;
        }

        // If the dates aren't set, make it a range that spans the last three years
        if (!this.maxDateRange.maxDate) this.maxDateRange.maxDate = moment();

        if (!this.maxDateRange.minDate) this.maxDateRange.minDate = moment().subtract(3, "year");

        // dateRange holds the dates that the user has set, which will be sent back to the server in the preflight request
        if (!this.dateRange) {
          this.dateRange = {};
          // For single-point sliders, default to showing the first period only, not all dates.
          if (this.sliderType == 1) {
            let interval = Interval.getNoun(this.sliderInterval);
            this.dateRange.minDate = this.maxDateRange.minDate.clone().startOf(interval);
            this.dateRange.maxDate = this.maxDateRange.minDate.clone().endOf(interval);
          }
        }
      };

      map.getLegends = function () {
        let legends = [];
        this.collections.forEach((collection) => {
          let theme = collection.findTheme(this);
          if (!theme.legend.length || !this.visible[collection.ID]) return;

          let entry = { entries: theme.legend, collectionID: collection.ID, theme };
          if (!theme.customLegendHeader || theme.showLegendHeader) entry.name = collection.name;

          if (theme.customLegendHeader) entry.name = theme.customLegendHeader;

          legends.push(entry);
        });

        legends.forEach(function (legend) {
          if (!legend.theme.showLegendHeader && legends.length <= 1) delete legend.name;

          delete legend.theme;
        });

        return legends;
      };

      /**
       * Setup the map for use by the frontend
       * @param  {array} collections
       * @return {void}
       */
      map.setup = function (collections) {
        // Create the collections array (it doesn't exist when fetched from the server, so it needs to be populated from the workspace)
        this.collections = [];

        // Loop through all the collectionIDs and push them on the map
        for (var i = 0; i < this.collectionIDs.length; i++) {
          var ID = this.collectionIDs[i];
          this.collections.push(_.find(collections, { ID: ID }));
        }

        if (this.compEnabled) this.compInterval = this.compInterval || "1 year";

        this.createDateRangeIfEnabled();
        this.legends = this.getLegends();
      };

      var preflightCanceler;

      /**
       * Prepare the map object to send in a preflight request.
       * @return {object}
       */
      map.getPreflightMap = function () {
        // Prep the preflighted map by only including the necessary properties
        var preflightMap = new ObjectFilter([{ collections: ["ID", "name", "tableName"] }, "entityID", "selectable"], this).getClean();

        // Strip all the non-visible collections from the preflighted map
        for (var ID in this.visible) {
          if (!this.visible[ID]) preflightMap.collections.splice(_.findIndex(preflightMap.collections, { ID: parseInt(ID) }), 1);
        }

        // Loop through all the map collections
        for (var i = 0; i < this.collections.length; i++) {
          // Set the collection so it's easy to access and find the theme
          var collection = this.collections[i],
            theme = collection.findTheme(this);

          // If it's a visible collection, add the theme & filters to the preflighted collection
          if (this.visible[collection.ID]) {
            var preflightCollection = _.find(preflightMap.collections, { ID: collection.ID });
            preflightCollection.theme = theme;
            preflightCollection.filters = this.filters[collection.ID] || [];
          }
        }

        // Make sure collections is defined
        if (!preflightMap.collections) preflightMap.collections = [];

        // Reorder all the collections so that the layers are rendered in the correct order
        var reorderedCollections = [];
        for (var i = preflightMap.collections.length - 1; i >= 0; i--) {
          reorderedCollections.push(preflightMap.collections[i]);
        }
        preflightMap.collections = reorderedCollections;

        if (this.dateEnabled) {
          preflightMap.minDate = this.dateRange.minDate ? this.dateRange.minDate.format("YYYY-MM-DD HH:mm:ss") : null;
          preflightMap.maxDate = this.dateRange.maxDate ? this.dateRange.maxDate.format("YYYY-MM-DD HH:mm:ss") : null;
          preflightMap.compInterval = this.compInterval || null;
        }

        return preflightMap;
      };

      /**
       * Preflighting for the map before rendering
       * @param  {boolean} [appendID] whether to append the map ID to the end of the instanceID
       * @return {promise}
       */
      map.sendRenderingPreflight = function (appendID = false) {
        // If there's a canceler for the preflight request, cancel it (by resolving the promise)
        if (preflightCanceler) preflightCanceler.resolve();

        // Create the promise for the preflight canceler
        // If the promise is resolved, the request will cancel, else it will continue
        preflightCanceler = $q.defer();

        const preflightMap = this.getPreflightMap();

        // If this is a public map, send the UUID in the URL
        var UUID = this.isPublic ? "/" + this.UUID : "";

        // Post the map to the preflight endpoint
        return $http
          .post(
            API_ROOT + "/mapping/maps/preflight/" + INSTANCE_ID + (appendID ? this.ID : "") + UUID + "?entityID=" + this.entityID,
            { map: preflightMap, password: this.password },
            { timeout: preflightCanceler.promise }
          )
          .then(function (response) {
            // Unset the preflight canceler
            preflightCanceler = null;
            return response.data.data;
          });
      };

      map.getImageDownloadUrl = function (zoom, sw, ne, marker) {
        var url = [TILE_URL, "tile", INSTANCE_ID, zoom, "snippet.png"],
          params = {
            user: MAPBOX_USER,
            styleID: MAPBOX_STYLE_ID,
            tileSize: MAPBOX_TILE_SIZE,
            highdpi: false,
            accessToken: MAPBOX_ACCESS_TOKEN,
            bbox: JSON.stringify([
              [sw.lng, sw.lat],
              [ne.lng, ne.lat],
            ]),
            marker: marker && JSON.stringify(marker),
          };

        return url.join("/") + "?" + $.param(params);
      };

      /**
       * Remove a given collection from this map
       * @param {int} collectionID
       * @return {void}
       */
      map.removeCollection = function (collectionID) {
        _.pull(this.collectionIDs, collectionID);
        _.remove(this.collections, { ID: collectionID });
        delete this.visible[collectionID];
        delete this.labels[collectionID];
      };

      /**
       * Create a new UUID for this map
       */
      map.getNewUUID = function () {
        return this.customPOST({}, "uuid").then((uuid) => {
          this.UUID = uuid;
          return uuid;
        });
      };

      /**
       * Get an expiring UUID
       * @param {string} expiresAt
       */
      map.getExpiringUUID = function (expiresAt) {
        return this.customPOST({ expiresAt }, "expiring-uuid");
      };

      /**
       * Retrieve the map's annotations.
       * @param {object} after A moment.js date object
       * @return {Promise}
       */
      map.getAnnotations = function (after) {
        let params = after ? { after: after.format() } : null;
        return this.customGET("annotations", params);
      };

      /**
       * Add a shape to the map's annotations layer.
       * @param {object} feature GeoJSON feature
       * @param {object} after A moment.js date object
       * @return {Promise}
       */
      map.addAnnotation = function (feature, after) {
        return this.customPOST(Object.assign(feature, { after: after.format() }), "annotations");
      };

      /**
       * Edit one or more shapes in the map's annotations layer.
       * @param {array} features Array of GeoJSON features
       * @param {object} after A moment.js date object
       * @return {Promise}
       */
      map.editAnnotations = function (features, after) {
        return this.customPUT(Object.assign(features, { after: after.format() }), "annotations");
      };

      /**
       * Remove a shape from the map's annotations layer.
       * @param {array} ids Array of feature IDs
       * @param {object} after A moment.js date object*
       * @return {Promise}
       */
      map.deleteAnnotations = function (ids, after) {
        return this.customDELETE("annotations", { "ids[]": ids, after: after.format() });
      };

      /**
       * Get a hashed value from the server which will be used to enable the annotations layer in a public link.
       * @return {Promise}
       */
      map.getAnnotationsOption = function () {
        return $http.get(API_ROOT + "/hash/?in=annotations=on_" + moment(this.createdAt).format("YYYY-MM-DD")).then(function (response) {
          return response.data.data;
        });
      };

      return map;
    });

    // add a custom methods to collections of collections
    Restangular.addElementTransformer("mapping/maps", true, function (maps) {
      // don't try to work on uninitialized collection
      if (!("one" in maps)) return maps;

      maps.addRestangularMethod("search", "getList", "search");

      return maps;
    });

    function _prepMapForSave(map) {
      var clonedMap = "clone" in map ? map.clone() : angular.copy(map);
      delete clonedMap.collections;
      return clonedMap;
    }

    Maps.getByUUID = function (UUID, params) {
      return Maps.customGET(UUID, params);
    };

    Maps.getEmptyMap = function (workspaceID, entityID) {
      let map = {
        name: "Untitled Map",
        collections: [],
        collectionIDs: [],
        visible: {},
        selectable: {},
        themes: {},
        labels: {},
        filters: {},
        entityID: entityID,
        workspaceID: workspaceID,
        dateRange: {},
        sliderType: 1,
        sliderInterval: 1,
        reports: [],
      };
      this.restangularize(map);
      return map;
    };

    /**
     * Restangularizes a map
     * @param  {object} map
     * @return {void}
     */
    Maps.restangularize = function (map) {
      Restangular.restangularizeElement(null, map, Maps.route);
    };

    /**
     * Strip everything off the object except for data.
     * (Note: not recursive.)
     */
    Maps.dataOnly = function (dirty) {
      return Object.keys(Restangular.stripRestangular(dirty))
        .filter((k) => typeof dirty[k] !== "function")
        .reduce((clean, k) => ({ ...clean, [k]: dirty[k] }), {});
    };

    Maps.intervals = ["1 month", "3 months", "6 months", "1 year", "2 years", "5 years"];
    Object.freeze(Maps.intervals);

    return Maps;
  },
]);
